import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Camera, Upload, Loader2, Utensils, Flame, 
  Apple, Drumstick, Wheat, Droplets, Heart,
  History, X, ChevronRight, Sparkles, Info, Crown, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || '/api';

export default function FoodAnalyzer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isEnglish = i18n.language === 'en';
  
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await axios.get(`${API}/api/ai/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsPremium(response.data?.is_premium === true);
      
      if (response.data?.is_premium) {
        fetchHistory();
      }
    } catch (error) {
      console.error('Error checking premium status:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/api/ai/food-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(isEnglish ? 'Please select an image file' : 'Lütfen bir resim dosyası seçin');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(isEnglish ? 'Image size must be less than 10MB' : 'Resim boyutu 10MB\'dan küçük olmalı');
      return;
    }

    setImage(file);
    setResult(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!image) {
      toast.error(isEnglish ? 'Please select an image first' : 'Önce bir resim seçin');
      return;
    }

    setAnalyzing(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(isEnglish ? 'Please login first' : 'Lütfen önce giriş yapın');
        return;
      }

      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(image);
      
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        
        try {
          const response = await axios.post(
            `${API}/api/ai/analyze-food`,
            {
              image_base64: base64,
              language: isEnglish ? 'en' : 'tr'
            },
            { 
              headers: { Authorization: `Bearer ${token}` },
              timeout: 60000 // 60 second timeout for AI analysis
            }
          );
          
          setResult(response.data);
          fetchHistory(); // Refresh history
          toast.success(isEnglish ? 'Analysis complete!' : 'Analiz tamamlandı!');
        } catch (error) {
          console.error('Analysis error:', error);
          if (error.response?.status === 403) {
            toast.error(error.response.data.detail || (isEnglish ? 'Daily limit reached' : 'Günlük limit doldu'));
          } else {
            toast.error(isEnglish ? 'Analysis failed. Please try again.' : 'Analiz başarısız. Tekrar deneyin.');
          }
        } finally {
          setAnalyzing(false);
        }
      };
    } catch (error) {
      console.error('Error:', error);
      setAnalyzing(false);
      toast.error(isEnglish ? 'An error occurred' : 'Bir hata oluştu');
    }
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const getHealthScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-yellow-600 bg-yellow-100';
    if (score >= 4) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Non-premium user: Show upgrade prompt
  if (!isPremium) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="border-yellow-400 bg-yellow-50">
            <CardContent className="p-8 text-center">
              <div className="bg-yellow-200 p-4 rounded-full inline-block mb-4">
                <Lock className="h-8 w-8 text-yellow-700" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {isEnglish ? 'Premium Feature' : 'Premium Özellik'}
              </h2>
              <p className="text-gray-600 mb-6">
                {isEnglish 
                  ? 'Food photo analysis is only available to Premium members. Upgrade now to unlock this feature.'
                  : 'Yemek fotoğrafı analizi sadece Premium üyelere açıktır. Bu özelliği açmak için Premium\'a yükseltin.'}
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button
                  onClick={() => navigate('/premium')}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {isEnglish ? 'Upgrade to Premium' : 'Premium\'a Yükselt'}
                </Button>
                <Link to="/ai-dietitian">
                  <Button variant="outline">
                    <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
                    {isEnglish ? 'Back' : 'Geri Dön'}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">AI {isEnglish ? 'Powered' : 'Destekli'}</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {isEnglish ? 'Food Photo Analyzer' : 'Yemek Fotoğrafı Analizi'}
          </h1>
          <p className="text-gray-600">
            {isEnglish 
              ? 'Take a photo of your plate and get instant calorie & nutrition analysis'
              : 'Tabağınızın fotoğrafını çekin, anında kalori ve besin analizi alın'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                {isEnglish ? 'Upload Photo' : 'Fotoğraf Yükle'}
              </CardTitle>
              <CardDescription>
                {isEnglish 
                  ? 'Take a photo or upload from gallery'
                  : 'Fotoğraf çekin veya galeriden yükleyin'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!imagePreview ? (
                <div className="space-y-4">
                  {/* Upload Area */}
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600 mb-2">
                      {isEnglish ? 'Click to upload or drag & drop' : 'Yüklemek için tıklayın veya sürükleyin'}
                    </p>
                    <p className="text-sm text-gray-400">
                      PNG, JPG, WEBP (max 10MB)
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isEnglish ? 'Gallery' : 'Galeri'}
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      {isEnglish ? 'Camera' : 'Kamera'}
                    </Button>
                  </div>
                  
                  {/* Hidden inputs */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Image Preview */}
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Food preview"
                      className="w-full h-64 object-cover rounded-xl"
                    />
                    <button
                      onClick={clearImage}
                      className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Analyze Button */}
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={analyzeImage}
                    disabled={analyzing}
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        {isEnglish ? 'Analyzing...' : 'Analiz ediliyor...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5 mr-2" />
                        {isEnglish ? 'Analyze Food' : 'Yemeği Analiz Et'}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="space-y-6">
            {result ? (
              <>
                {/* Total Calories Card */}
                <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">
                          {isEnglish ? 'Total Calories' : 'Toplam Kalori'}
                        </p>
                        <p className="text-4xl font-bold">
                          {result.total_calories || 0}
                        </p>
                        <p className="text-orange-100 text-sm">kcal</p>
                      </div>
                      <div className="p-4 bg-white/20 rounded-xl">
                        <Flame className="h-10 w-10" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Macros */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">
                      {isEnglish ? 'Macronutrients' : 'Makro Besinler'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-blue-50 rounded-xl">
                        <Drumstick className="h-6 w-6 mx-auto mb-1 text-blue-600" />
                        <p className="text-2xl font-bold text-blue-600">
                          {result.macros?.protein || 0}g
                        </p>
                        <p className="text-xs text-gray-500">
                          {isEnglish ? 'Protein' : 'Protein'}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-xl">
                        <Wheat className="h-6 w-6 mx-auto mb-1 text-yellow-600" />
                        <p className="text-2xl font-bold text-yellow-600">
                          {result.macros?.carbs || 0}g
                        </p>
                        <p className="text-xs text-gray-500">
                          {isEnglish ? 'Carbs' : 'Karbonhidrat'}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-xl">
                        <Droplets className="h-6 w-6 mx-auto mb-1 text-purple-600" />
                        <p className="text-2xl font-bold text-purple-600">
                          {result.macros?.fat || 0}g
                        </p>
                        <p className="text-xs text-gray-500">
                          {isEnglish ? 'Fat' : 'Yağ'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Foods Detected */}
                {result.foods?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Utensils className="h-5 w-5" />
                        {isEnglish ? 'Foods Detected' : 'Tespit Edilen Yiyecekler'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {result.foods.map((food, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{food.name}</p>
                              <p className="text-sm text-gray-500">{food.portion}</p>
                            </div>
                            <Badge variant="outline" className="text-orange-600 border-orange-200">
                              {food.calories} kcal
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Health Score & Tips */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`p-3 rounded-xl ${getHealthScoreColor(result.health_score || 5)}`}>
                        <Heart className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          {isEnglish ? 'Health Score' : 'Sağlık Puanı'}
                        </p>
                        <p className="text-2xl font-bold">{result.health_score || 5}/10</p>
                      </div>
                    </div>
                    
                    {result.tips?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-600">
                          {isEnglish ? 'Tips' : 'Öneriler'}:
                        </p>
                        {result.tips.map((tip, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <Info className="h-4 w-4 mt-0.5 text-purple-500" />
                            <span>{tip}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {result.description && (
                      <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
                        {result.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <Utensils className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">
                    {isEnglish 
                      ? 'Upload a food photo to see analysis results'
                      : 'Analiz sonuçlarını görmek için bir yemek fotoğrafı yükleyin'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  {isEnglish ? 'Recent Analyses' : 'Son Analizler'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  {showHistory 
                    ? (isEnglish ? 'Hide' : 'Gizle')
                    : (isEnglish ? 'Show All' : 'Tümünü Göster')}
                </Button>
              </div>
            </CardHeader>
            {showHistory && (
              <CardContent>
                <div className="space-y-3">
                  {history.slice(0, 10).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">
                          {item.result?.foods?.map(f => f.name).join(', ') || 
                           (isEnglish ? 'Food Analysis' : 'Yemek Analizi')}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(item.created_at)}</p>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700">
                        {item.result?.total_calories || 0} kcal
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Back to AI Dietitian */}
        <div className="mt-8 text-center">
          <Link to="/ai-dietitian">
            <Button variant="outline">
              <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
              {isEnglish ? 'Back to AI Dietitian' : 'AI Diyetisyene Dön'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
